import { useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2, Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { useUpdatePatient } from '@/hooks/usePatients'
import { useToast } from '@/hooks/use-toast'
import type { Patient } from '@/types'

const insuranceContactsSchema = z.object({
  insurance_info: z.array(
    z.object({
      provider: z.string().min(1, 'El proveedor es obligatorio'),
      policy_number: z.string().min(1, 'El número de póliza es obligatorio'),
      group_number: z.string().optional(),
      subscriber_name: z.string().optional(),
      subscriber_relationship: z.string().optional(),
      effective_date: z.string().optional(),
      expiration_date: z.string().optional(),
      copay: z.coerce.number().optional(),
      is_primary: z.boolean().default(false),
    })
  ),
  emergency_contacts: z.array(
    z.object({
      name: z.string().min(1, 'El nombre es obligatorio'),
      relationship: z.string().min(1, 'La relación es obligatoria'),
      phone: z.string().min(5, 'El teléfono es obligatorio'),
      email: z.string().optional(),
      address: z.string().optional(),
      is_primary: z.boolean().default(false),
    })
  ),
})

type InsuranceContactsFormValues = z.infer<typeof insuranceContactsSchema>

interface EditInsuranceContactsDialogProps {
  patient: Patient
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdated?: (patient: Patient) => void
}

function getDefaultValues(patient: Patient): InsuranceContactsFormValues {
  return {
    insurance_info: patient.insurance_info ?? [],
    emergency_contacts:
      patient.emergency_contacts && patient.emergency_contacts.length > 0
        ? patient.emergency_contacts
        : [],
  }
}

export function EditInsuranceContactsDialog({
  patient,
  open,
  onOpenChange,
  onUpdated,
}: EditInsuranceContactsDialogProps) {
  const { toast } = useToast()
  const { mutate: updatePatient, isPending } = useUpdatePatient()

  const form = useForm<InsuranceContactsFormValues>({
    resolver: zodResolver(insuranceContactsSchema),
    defaultValues: getDefaultValues(patient),
  })

  useEffect(() => {
    if (patient) {
      form.reset(getDefaultValues(patient))
    }
  }, [patient, form])

  const insuranceFields = useFieldArray({
    control: form.control,
    name: 'insurance_info',
  })

  const contactFields = useFieldArray({
    control: form.control,
    name: 'emergency_contacts',
  })

  function onSubmit(data: InsuranceContactsFormValues) {
    updatePatient(
      {
        id: patient.id,
        data: {
          insurance_info: data.insurance_info,
          emergency_contacts: data.emergency_contacts,
        },
      },
      {
        onSuccess: (updatedPatient) => {
          onOpenChange(false)
          onUpdated?.(updatedPatient)
          toast({
            title: 'Información actualizada',
            description:
              'Los seguros y contactos de emergencia se guardaron correctamente.',
          })
        },
        onError: (error) => {
          toast({
            title: 'No pudimos guardar los cambios',
            description:
              error.message ||
              'Ocurrió un problema al actualizar los seguros y contactos de emergencia.',
            variant: 'destructive',
          })
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Seguros y Contactos</DialogTitle>
          <DialogDescription>
            Actualiza la información de seguros y contactos de emergencia del
            paciente.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Seguros */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Seguros</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    insuranceFields.append({
                      provider: '',
                      policy_number: '',
                      group_number: '',
                      subscriber_name: '',
                      subscriber_relationship: '',
                      effective_date: '',
                      expiration_date: '',
                      copay: undefined,
                      is_primary: false,
                    })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Agregar
                </Button>
              </div>

              {insuranceFields.fields.map((field, index) => (
                <div
                  key={field.id}
                  className="border p-4 rounded-md bg-muted/20 space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      Seguro {index + 1}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => insuranceFields.remove(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name={`insurance_info.${index}.provider`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Proveedor</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Nombre del asegurador"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`insurance_info.${index}.policy_number`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">
                            Número de Póliza
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Número de póliza" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`insurance_info.${index}.group_number`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">
                            Número de Grupo
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Grupo" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`insurance_info.${index}.subscriber_name`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Titular</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Nombre del titular"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`insurance_info.${index}.effective_date`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">
                            Vigencia Desde
                          </FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`insurance_info.${index}.expiration_date`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">
                            Vigencia Hasta
                          </FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`insurance_info.${index}.copay`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Copago</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="0.00"
                              {...field}
                              value={field.value ?? ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`insurance_info.${index}.is_primary`}
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-2 space-y-0 mt-6">
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  const insurances = form.getValues('insurance_info')
                                  form.setValue(
                                    'insurance_info',
                                    insurances.map((insurance, insuranceIndex) => ({
                                      ...insurance,
                                      is_primary: insuranceIndex === index,
                                    }))
                                  )
                                  return
                                }

                                field.onChange(false)
                              }}
                            />
                          </FormControl>
                          <FormLabel className="text-xs">
                            Seguro Principal
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Contactos de Emergencia */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">
                  Contactos de Emergencia
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    contactFields.append({
                      name: '',
                      relationship: '',
                      phone: '',
                      email: '',
                      address: '',
                      is_primary: false,
                    })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Agregar
                </Button>
              </div>

              {contactFields.fields.map((field, index) => (
                <div
                  key={field.id}
                  className="border p-4 rounded-md bg-muted/20 space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      Contacto {index + 1}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => contactFields.remove(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name={`emergency_contacts.${index}.name`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Nombre</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Nombre del contacto"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`emergency_contacts.${index}.relationship`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Relación</FormLabel>
                          <FormControl>
                            <Input placeholder="ej. Cónyuge" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`emergency_contacts.${index}.phone`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Teléfono</FormLabel>
                          <FormControl>
                            <Input placeholder="Teléfono" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`emergency_contacts.${index}.email`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">
                            Correo Electrónico
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="correo@ejemplo.com"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`emergency_contacts.${index}.address`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Dirección</FormLabel>
                          <FormControl>
                            <Input placeholder="Dirección" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`emergency_contacts.${index}.is_primary`}
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-2 space-y-0 mt-6">
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  const contacts = form.getValues('emergency_contacts')
                                  form.setValue(
                                    'emergency_contacts',
                                    contacts.map((contact, contactIndex) => ({
                                      ...contact,
                                      is_primary: contactIndex === index,
                                    }))
                                  )
                                  return
                                }

                                field.onChange(false)
                              }}
                            />
                          </FormControl>
                          <FormLabel className="text-xs">
                            Contacto Principal
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="gradient-medical hover:opacity-90 transition-opacity shadow-lg shadow-medical-500/25"
                disabled={isPending}
              >
                {isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Guardar cambios
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
